import {
  useListScenes,
  useActivateScene,
  useDeactivateScene,
  getListScenesQueryKey,
  type ScenesSceneSummaryResponse,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  Zap,
  RefreshCw,
  AlertCircle,
  Plus,
  Clock,
  ArrowUpDown,
  Play,
  Square,
  ArrowDownAZ,
  ListOrdered,
  History,
} from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Badge, Button, Dropdown, IconButton, Tooltip, useToast } from '@/components/ui';
import { useScenesStream } from '@/features/scenes/hooks/useScenesStream';
import { formatDurationShort, formatTimeRemaining } from '@/features/scenes/utils/duration';

type SortOption = 'priority' | 'name' | 'recent';

/**
 * ScenesPage - API-driven scenes list with activation controls
 */
const SORT_OPTIONS = [
  { value: 'priority' as SortOption, label: 'Priority', icon: ListOrdered },
  { value: 'name' as SortOption, label: 'Name', icon: ArrowDownAZ },
  { value: 'recent' as SortOption, label: 'Recent activation', icon: History },
];

export function ScenesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [activatingSceneId, setActivatingSceneId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('priority');

  // Connect to WebSocket for live updates
  const { isConnected, connectionFailed } = useScenesStream();

  const { data, isLoading, isError, refetch, isFetching } = useListScenes({
    query: {
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
    },
  });

  const { mutate: activateScene } = useActivateScene({
    mutation: {
      onMutate: ({ id }) => {
        setActivatingSceneId(id);
      },
      onSuccess: (_data, { id }) => {
        const scene = scenes.find((s) => s.id === id);
        const isInstant = scene?.activationMode === 'instant';
        addToast({
          message: `Scene "${scene?.name ?? 'Unknown'}" ${isInstant ? 'executed' : 'activated'}`,
          variant: 'success',
        });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to activate scene',
          variant: 'error',
        });
      },
      onSettled: () => {
        setActivatingSceneId(null);
      },
    },
  });

  const { mutate: deactivateScene } = useDeactivateScene({
    mutation: {
      onMutate: ({ id }) => {
        setActivatingSceneId(id);
      },
      onSuccess: (_data, { id }) => {
        const scene = scenes.find((s) => s.id === id);
        addToast({
          message: `Scene "${scene?.name ?? 'Unknown'}" deactivated`,
          variant: 'info',
        });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to deactivate scene',
          variant: 'error',
        });
      },
      onSettled: () => {
        setActivatingSceneId(null);
      },
    },
  });

  const scenes = useMemo(() => data?.scenes ?? [], [data?.scenes]);

  // Sort scenes based on selected sort option
  const sortedScenes = useMemo(() => {
    const sorted = [...scenes];
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      case 'recent':
        // Sort by activeUntil (currently active) then by whether they're active
        // Active scenes first, then by priority as fallback
        return sorted.sort((a, b) => {
          // Active scenes first
          if (a.active && !b.active) return -1;
          if (!a.active && b.active) return 1;
          // Both active or both inactive - sort by priority
          return (b.priority ?? 0) - (a.priority ?? 0);
        });
      case 'priority':
      default:
        return sorted.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    }
  }, [scenes, sortBy]);

  const handleQuickActivate = useCallback(
    (scene: ScenesSceneSummaryResponse) => {
      if (!scene.id) return;
      activateScene({ id: scene.id, data: {} });
    },
    [activateScene]
  );

  const handleDeactivate = useCallback(
    (scene: ScenesSceneSummaryResponse) => {
      if (!scene.id) return;
      deactivateScene({ id: scene.id });
    },
    [deactivateScene]
  );

  const getMetadataLine = (scene: ScenesSceneSummaryResponse) => {
    const parts: string[] = [];
    parts.push(`${scene.deviceCount} device${scene.deviceCount !== 1 ? 's' : ''}`);
    if (scene.priority !== undefined && scene.priority > 0) {
      parts.push(`Priority ${scene.priority}`);
    }
    // Only show duration label if NOT an active fromTriggerEnd scene (that case is handled separately)
    if (scene.durationSeconds && !(scene.active && scene.durationMode === 'fromTriggerEnd')) {
      const mode = scene.durationMode === 'fromTriggerEnd' ? 'Stop delay' : 'Fixed';
      parts.push(`${mode}: ${formatDurationShort(scene.durationSeconds)}`);
    }
    return parts.join(' · ');
  };

  const getTimeRemainingLabel = (activeUntil?: string) => {
    if (!activeUntil) return null;
    const remaining = formatTimeRemaining(activeUntil);
    if (remaining === null) return 'Ending...';
    return remaining;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-shrink-0 items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Scenes
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            {isLoading
              ? 'Loading...'
              : `${scenes.length} scene${scenes.length !== 1 ? 's' : ''} configured`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Live connection indicator */}
          {!connectionFailed && (
            <div
              className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500"
              title={isConnected ? 'Live updates active' : 'Connecting...'}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-gray-400'}`}
              />
              <span>{isConnected ? 'Live' : '...'}</span>
            </div>
          )}

          {/* Sort dropdown */}
          <Dropdown
            trigger={
              <Button variant="outline" size="sm">
                {(() => {
                  const opt = SORT_OPTIONS.find((o) => o.value === sortBy);
                  const Icon = opt?.icon ?? ListOrdered;
                  return (
                    <>
                      <Icon className="mr-2 h-4 w-4" />
                      {opt?.label ?? 'Sort'}
                    </>
                  );
                })()}
              </Button>
            }
            align="right"
            value={sortBy}
            onSelect={(value) => setSortBy(value as SortOption)}
            items={SORT_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
              icon: <opt.icon className="h-4 w-4" />,
            }))}
          />

          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Tooltip content="Set priority order for conflict resolution when multiple scenes control the same device">
            <Link to="/scenes/reorder">
              <Button variant="outline" size="sm">
                <ArrowUpDown className="mr-2 h-4 w-4" />
                Priorities
              </Button>
            </Link>
          </Tooltip>
          <Link to="/scenes/new">
            <Button size="sm">
              <Plus className="mr-2 h-4 w-4" />
              New Scene
            </Button>
          </Link>
        </div>
      </div>

      {/* Scene List */}
      <div className="min-h-48 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load scenes</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 text-sm text-blue-500 hover:text-blue-400"
            >
              Try again
            </button>
          </div>
        ) : sortedScenes.length === 0 ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
              <Zap className="h-6 w-6 text-gray-500 dark:text-zinc-500" />
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">No scenes configured yet</p>
            <Link
              to="/scenes/new"
              className="mt-3 inline-block text-sm text-blue-500 hover:text-blue-400"
            >
              Create your first scene
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
            {sortedScenes.map((scene) => (
              <SceneRow
                key={scene.id}
                scene={scene}
                isActivating={activatingSceneId === scene.id}
                onActivate={() => handleQuickActivate(scene)}
                onDeactivate={() => handleDeactivate(scene)}
                onNavigate={() => navigate(`/scenes/${scene.id}`)}
                getMetadataLine={getMetadataLine}
                getTimeRemaining={getTimeRemainingLabel}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface SceneRowProps {
  scene: ScenesSceneSummaryResponse;
  isActivating: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onNavigate: () => void;
  getMetadataLine: (scene: ScenesSceneSummaryResponse) => string;
  getTimeRemaining: (activeUntil?: string) => string | null;
}

function SceneRow({
  scene,
  isActivating,
  onActivate,
  onDeactivate,
  onNavigate,
  getMetadataLine,
  getTimeRemaining,
}: SceneRowProps) {
  const isActive = scene.active;
  const isInstant = scene.activationMode === 'instant';
  const timeRemaining = getTimeRemaining(scene.activeUntil);

  return (
    <div
      className={`group flex items-center gap-4 px-4 py-3 transition-colors ${
        isActive
          ? 'border-l-2 border-l-blue-500 bg-blue-50/50 dark:bg-blue-500/5'
          : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02]'
      }`}
    >
      {/* Clickable area for navigation */}
      <div
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-4"
        onClick={onNavigate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onNavigate();
          }
        }}
      >
        {/* Icon tile */}
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
            isActive
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'
              : 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400'
          }`}
        >
          <Zap className="h-5 w-5" />
        </div>

        {/* Text block */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
              {scene.name}
            </span>
            {/* Status badges inline with name */}
            {!scene.enabled && (
              <Badge variant="default" size="sm">
                Disabled
              </Badge>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
            <span className="truncate">{getMetadataLine(scene)}</span>
            {/* Active fromTriggerEnd mode - show special text */}
            {isActive && scene.durationMode === 'fromTriggerEnd' && scene.durationSeconds && (
              <>
                <span>·</span>
                <span>Stops {formatDurationShort(scene.durationSeconds)} after triggers end</span>
              </>
            )}
            {/* Time remaining for active scenes */}
            {isActive && timeRemaining && (
              <>
                <span>·</span>
                <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Clock className="h-3 w-3" />
                  {timeRemaining}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right cluster: action controls */}
      <div
        className="flex flex-shrink-0 items-center gap-1 self-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Action button - subtle styling */}
        <IconButton
          variant="secondary"
          size="sm"
          onClick={isInstant ? onActivate : isActive ? onDeactivate : onActivate}
          loading={isActivating}
          icon={
            isInstant ? (
              <Play fill="currentColor" strokeWidth={0} />
            ) : isActive ? (
              <Square fill="currentColor" strokeWidth={0} />
            ) : (
              <Play fill="currentColor" strokeWidth={0} />
            )
          }
          label={isInstant ? 'Run scene' : isActive ? 'Deactivate scene' : 'Activate scene'}
          className="rounded-md"
        />
      </div>
    </div>
  );
}
